import { supabase, TABLES } from './supabase';
import { User } from '../types';

export class AuthService {
  static async signIn(email: string, password: string) {
    try {
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        return { user: null, error: 'Supabase not configured. Please set up your environment variables.' };
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { user: null, error: error.message };
      }

      if (!data.user) {
        return { user: null, error: 'No user data returned' };
      }

      // Get user profile from our users table
      const { data: userData, error: userError } = await supabase
        .from(TABLES.USERS)
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (userError) {
        console.error('Error fetching user profile:', userError);
        return { user: null, error: 'Failed to fetch user profile' };
      }

      return { user: userData, error: null };
    } catch (error) {
      console.error('AuthService.signIn error:', error);
      return { 
        user: null, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  }

  static async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
        return { error: error.message };
      }
      return { error: null };
    } catch (error) {
      console.error('AuthService.signOut error:', error);
      return { 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  }

  static async getCurrentUser(): Promise<{ user: User | null; error: string | null }> {
    try {
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        console.warn('AuthService: Supabase not configured, returning null user');
        return { user: null, error: null };
      }

      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('Auth error:', authError);
        return { user: null, error: authError.message };
      }

      if (!authUser) {
        return { user: null, error: null };
      }

      // Get user profile from our users table
      const { data: userData, error: userError } = await supabase
        .from(TABLES.USERS)
        .select('*')
        .eq('id', authUser.id)
        .single();

      if (userError) {
        console.error('Error fetching user profile:', userError);
        return { user: null, error: userError.message };
      }

      return { user: userData, error: null };
    } catch (error) {
      console.error('AuthService.getCurrentUser error:', error);
      return { 
        user: null, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  }

  static onAuthStateChange(callback: (user: User | null) => void) {
    return supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        const { user } = await this.getCurrentUser();
        callback(user);
      } else {
        callback(null);
      }
    });
  }

  static async signUp(email: string, password: string, role: 'admin' | 'staff' = 'staff') {
    try {
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        return { user: null, error: 'Supabase not configured. Please set up your environment variables.' };
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password
      });

      if (error) {
        return { user: null, error: error.message };
      }

      if (!data.user) {
        return { user: null, error: 'No user data returned' };
      }

      // Update user role in our users table
      const { error: updateError } = await supabase
        .from(TABLES.USERS)
        .update({ role })
        .eq('id', data.user.id);

      if (updateError) {
        console.error('Error updating user role:', updateError);
        return { user: null, error: 'Failed to set user role' };
      }

      return { user: data.user, error: null };
    } catch (error) {
      console.error('AuthService.signUp error:', error);
      return { 
        user: null, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  }

  static async resetPassword(email: string) {
    try {
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        return { error: 'Supabase not configured. Please set up your environment variables.' };
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return { error: error.message };
      }

      return { error: null };
    } catch (error) {
      console.error('AuthService.resetPassword error:', error);
      return { 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  }

  static async updatePassword(newPassword: string) {
    try {
      if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
        return { error: 'Supabase not configured. Please set up your environment variables.' };
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        return { error: error.message };
      }

      return { error: null };
    } catch (error) {
      console.error('AuthService.updatePassword error:', error);
      return { 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  }
}